import {Box, Counter, Flex, Image, Label, P} from 'exsportia-components'
import {FC} from 'react'

import {OfferedAddon} from '../../../api/addons/types'
import {addonPriceForQuantity, addonPriceSteps} from '../../../helpers/addon-price'
import {safeRound} from '../../../helpers/logic'
import {TranslatorScope} from '../../../locale/types'
import {useLocale} from '../../../utils/i18n'

type AddonRowProps = {
  addon: OfferedAddon
  currency?: string
  quantity: number
  // The most this participant may take (already limited by what the other participants hold).
  max: number
  onChange: (quantity: number) => void
}

// One offered add-on: photo, name, what it is, its price in words, and a counter (or "Included").
// The figures here are only for display while choosing; the booking is charged what the server quotes.
export const AddonRow: FC<AddonRowProps> = ({addon, currency, quantity, max, onChange}) => {
  const {l} = useLocale()
  const money = (value: number) => l(`${currency || 'USD'}_symbolic` as TranslatorScope, {value: safeRound(value)})
  const steps = addonPriceSteps(addon)
  const priceInWords =
    steps.length === 1 && steps[0].total === 0
      ? l('free')
      : steps
          .map((step, index) =>
            index === 0
              ? money(step.total)
              : l('addon_price_tier', {quantity: step.quantity, price: money(step.total)}),
          )
          .join(' · ')
  const chosenTotal = addon.included || quantity < 1 ? null : addonPriceForQuantity(addon, quantity)
  return (
    <Flex
      justifyContent='space-between'
      alignItems='center'
      mb='16px'
      gap='12px'
      position='relative'
      data-testid={`addon-row-${addon.addonId}`}
    >
      <Flex
        gap='12px'
        alignItems='center'
        maxWidth={['60%', '70%']}
      >
        {addon.photo && (
          <Box
            width='48px'
            height='48px'
            minWidth='48px'
            borderRadius='8px'
            overflow='hidden'
          >
            <Image
              src={addon.photo}
              width='100%'
              height='48px'
            />
          </Box>
        )}
        <Flex flexDirection='column'>
          <Label settings={{fontSize: '16px'}}>{addon.name}</Label>
          <P color='text-secondary'>{l(`addon_kind_${addon.kind}` as TranslatorScope)}</P>
          {addon.description && <P color='text-secondary'>{addon.description}</P>}
          <P>{priceInWords}</P>
          {addon.scope === 'PER_BOOKING' && <P color='text-secondary'>{l('addon_per_booking_hint')}</P>}
        </Flex>
      </Flex>
      {addon.included ? (
        <Label>{l('addon_included')}</Label>
      ) : (
        <Flex
          flexDirection='column'
          alignItems='flex-end'
          gap='4px'
        >
          <Counter
            min={0}
            max={max}
            value={quantity}
            onClickMinus={() => onChange(Math.max(0, quantity - 1))}
            onClickPlus={() => onChange(Math.min(max, quantity + 1))}
          />
          {chosenTotal !== null && (
            <P color='text-secondary'>{l('addon_price_tier', {quantity, price: money(chosenTotal)})}</P>
          )}
          {max > 0 && <P color='text-secondary'>{l('addon_max_hint', {max})}</P>}
        </Flex>
      )}
    </Flex>
  )
}
